import Highcharts from 'highcharts'
import Exporting from 'highcharts/modules/exporting'

import { defaultColors } from '@aytm/app/insights/dashboard/lib/colors'
import { defaultChartMenuItems } from '@aytm/app/insights/dashboard/lib/charts'
import { saveFullscreenSeriesState, restoreFullscreenSeriesState } from './fullscreenSeriesState'
import pointFormat from './SplitChart.pointFormat.html'

Exporting(Highcharts)

export const showCollapsedSections = false
export const showAttributeMetrics = true
export const showUniqueAttributes = true

const configNormal = {
  chart: {
    marginLeft: 26,
    marginRight: -26
  },
  legend: {
    enabled: false,
    maxHeight: 400
  },
  xAxis: {
    left: 0,
    labels: {
      enabled: false
    },
    lineWidth: 0
  },
  yAxis: {
    labels: {
      enabled: true,
      x: -4
    }
  }
}

const configFull = {
  chart: {
    marginLeft: 300,
    marginRight: 16
  },
  legend: {
    enabled: true,
    maxHeight: undefined,
    itemWidth: 220
  },
  xAxis: {
    left: undefined,
    labels: {
      enabled: true
    },
    lineWidth: 0
  },
  yAxis: {
    labels: {
      enabled: true,
      x: -4
    }
  }
}

const configLegend = {
  floating: false,
  layout: 'vertical',
  verticalAlign: 'left',
  itemMarginTop: 6,
  itemMarginBottom: 2,
  align: 'left',
  backgroundColor: '#ffffff',
  itemHiddenStyle: { color: '#cccccc' },
  maxHeight: 400,
  margin: 12,
  x: 10,
  y: 10,
  symbolHeight: 14,
  symbolWidth: 14,
  symbolRadius: 6,
  itemStyle: {
    fontSize: 14,
    fontWeight: 'normal'
  }
}

export const getChartParams = ({
  series,
  scale = '0-100',
  title = '',
  chartType,
  chartTypes = [],
  setChartType,
  setFullScreen,
  legendItemClick,
  onLegendMouseEnter,
  onLegendMouseLeave,
  marginTop = 32,
  isSharedView
} = {}) => {
  const hasData = series?.length

  const seriesOptions = chartType?.options?.series || {}
  seriesOptions.visible = Boolean(hasData)

  const legendOptions = chartType?.options?.legend || {}
  legendOptions.itemHiddenStyle = hasData ? configLegend.itemHiddenStyle : { color: configLegend.backgroundColor }

  return [
    {
      chart: {
        type: chartType.type,
        animation: false,
        marginTop,
        events: {
          fullscreenClose: function () {
            restoreFullscreenSeriesState(this)
            this.update({
              ...configNormal,
              exporting: {
                enabled: !isSharedView,
                buttons: {
                  contextButton: {
                    menuItems: [
                      ...chartTypes.filter(v => chartType.name !== v.name).map(v => v.name),
                      ...defaultChartMenuItems
                    ]
                  }
                }
              }
            })
            this.reflow()
            if (setFullScreen) {
              setFullScreen(false)
            }
          },
          fullscreenOpen: function () {
            saveFullscreenSeriesState(this)
            this.update({
              ...configFull,
              exporting: {
                enabled: true, // !isSharedView,
                buttons: {
                  contextButton: {
                    menuItems: [
                      {
                        textKey: 'exitFullscreen',
                        onclick: function () {
                          this.fullscreen.toggle()
                        }
                      },
                      {
                        separator: true // Adds a separator line
                      }
                    ].concat([
                      ...chartTypes.filter(v => chartType.name !== v.name).map(v => v.name),
                      ...defaultChartMenuItems.filter(v => v !== 'viewFullscreen' && v !== 'printChart')
                    ])
                  }
                }
              }
            })
            if (setFullScreen) {
              setFullScreen(true)
            }
          }
        },
        ...configNormal.chart
      },
      title: {
        text: title,
        y: 24,
        style: {
          textOverflow: 'ellipsis',
          overflowWrap: 'anywhere',
          fontSize: 20,
          color: '#444'
        }
      },
      colors: defaultColors,
      credits: {
        enabled: false
      },
      xAxis: {
        type: 'category',
        crosshair: true,
        ...configNormal.xAxis
      },
      yAxis: {
        min: 'min-max' === scale && hasData ? null : 0,
        max: 'min-max' === scale && hasData ? null : 100,
        title: {
          text: '%',
          enabled: false
        },
        ...configNormal.yAxis
      },
      tooltip: {
        outside: true,
        useHTML: true,
        headerFormat: null,
        formatter: function () {
          const pointElement = this.point.graphic.parentGroup.element
          const pointRect = pointElement.getBoundingClientRect()

          const viewportElement = pointElement.closest('.insights-portal-split-chart-container').parentElement // Viewport element
          const viewportRect = viewportElement.getBoundingClientRect()

          const obscuringElement = viewportElement.querySelector('.insights-portal-split-chart-legend') // Obscuring element
          const obscuringRect = obscuringElement ? obscuringElement.getBoundingClientRect() : null

          // Check if the chart is visible within the viewport
          const isPointLeftSideGreaterThanViewportLeftSide =
            pointRect.left > viewportRect.left + (obscuringRect?.width ?? 0)
          const isPointLeftSideLessThanViewportRightSide = pointRect.left < viewportRect.right
          const isPointRightSideGreaterThanViewportLeftSide =
            pointRect.right > viewportRect.left + (obscuringRect?.width ?? 0)
          const isPointRightSideLessThanViewportRightSide = pointRect.right < viewportRect.right

          const _isFullyVisible =
            isPointLeftSideGreaterThanViewportLeftSide && isPointRightSideLessThanViewportRightSide

          const isPartiallyVisible =
            isPointLeftSideLessThanViewportRightSide && isPointRightSideGreaterThanViewportLeftSide

          if (!isPartiallyVisible) {
            return false // Suppress the tooltip
          }

          // Default tooltip behavior
          return Highcharts.format(pointFormat, this)
        },
        pointFormat: pointFormat,
        style: {
          whiteSpace: 'normal',
          maxWidth: '10rem' // Adjust as needed
        },
        borderColor: '#666666'
      },
      legend: {
        ...configLegend,
        ...configNormal.legend,
        ...legendOptions
      },
      exporting: {
        enabled: !isSharedView,
        menuItemDefinitions: chartTypes.reduce((acc, cur) => {
          if ('0-100' !== scale || ('0-100' === scale && cur.name !== 'stacked area'))
            acc[cur.name] = {
              onclick: () => {
                if (setChartType) setChartType(cur)
                dispatchEvent(new Event('click', { bubbles: true }))
              },
              text: `Switch to ${cur.name} chart`
            }
          return acc
        }, {}),
        buttons: {
          contextButton: {
            menuItems: [
              ...chartTypes.filter(v => chartType.name !== v.name).map(v => v.name),
              ...defaultChartMenuItems
            ],
            useHTML: true // Allows the button to be styled with custom CSS
          }
        }
      },
      plotOptions: {
        line: {
          animation: false,
          states: {
            hover: {
              enabled: true
            },
            inactive: {
              enabled: true,
              opacity: 0.2
            }
          },
          events: {
            legendItemClick,
            onMouseEnter: onLegendMouseEnter,
            onMouseLeave: onLegendMouseLeave
          }
        },
        series: {
          animation: false,
          groupPadding: 0.1,
          pointPadding: 0.3,
          events: { legendItemClick },
          ...seriesOptions
        }
      },
      series: hasData ? series : [{}]
    },
    function (chart) {
      const exportButton = chart.exportSVGElements?.[0]
      if (!exportButton || !exportButton.element) {
        return
      }
      exportButton.element.style.visibility = 'hidden'
      exportButton.element.style.pointerEvents = 'none'

      if (!chart.container) {
        return
      }

      const button = chart.container.parentElement.parentElement.querySelector('.export-button')
      if (!button) {
        return
      }
      // The next line is the missing "button.removeEventListener('click')" implementation
      button.replaceWith(button.cloneNode(true))
      const nextButton = chart.container.parentElement.parentElement.querySelector('.export-button')
      nextButton.addEventListener('click', function (evt) {
        const exportButton = chart.exportSVGElements?.[0]
        exportButton.element.dispatchEvent(new MouseEvent('click', { bubbles: true, target: evt.target }))
        window.setTimeout(() => {
          const button = evt.target.closest('button')
          if (!button) {
            return
          }
          const chart = button.parentElement.parentElement.querySelector('[data-highcharts-chart]')
          if (!chart) {
            return
          }
          const menu = chart.parentElement.querySelector('.highcharts-contextmenu')
          if (menu && button) {
            const chartRect = chart.getBoundingClientRect()
            const buttonRect = button.getBoundingClientRect()
            const nextMenuOffset = {
              top: Math.floor(buttonRect.top - chartRect.top + buttonRect.height - 22),
              right: Math.floor(chartRect.width - (buttonRect.left - chartRect.left) - buttonRect.width - 23)
            }
            menu.style.position = 'absolute'
            menu.style.top = `${nextMenuOffset.top}px`
            menu.style.right = `${nextMenuOffset.right}px`
          }
        }, 0)
      })
    }
  ]
}

export const renderChart = ({
  id,
  series,
  scale = '0-100',
  title = '',
  chartType,
  chartTypes = [],
  setChartType,
  setFullScreen,
  legendItemClick,
  onLegendMouseEnter,
  onLegendMouseLeave,
  marginTop = 32,
  isSharedView
} = {}) => {
  const [options, callback] = getChartParams({
    series,
    scale,
    title,
    chartType,
    chartTypes,
    setChartType,
    setFullScreen,
    legendItemClick,
    onLegendMouseEnter,
    onLegendMouseLeave,
    marginTop,
    isSharedView
  })
  return Highcharts.chart(id, options, callback)
}

export const updateChart = ({
  chart = null,
  series,
  scale = '0-100',
  title = '',
  chartType,
  chartTypes = [],
  setChartType,
  setFullScreen,
  legendItemClick,
  onLegendMouseEnter,
  onLegendMouseLeave,
  marginTop = 32,
  isSharedView
} = {}) => {
  const [options] = getChartParams({
    series,
    scale,
    title,
    chartType,
    chartTypes,
    setChartType,
    setFullScreen,
    legendItemClick,
    onLegendMouseEnter,
    onLegendMouseLeave,
    marginTop,
    isSharedView
  })
  chart.update(options, true, true, true)
  return chart
}

export const renderLegend = ({ id, series, hideMarkers = false, onEnter, onLeave, onClick } = {}) => {
  const legendOptions = hideMarkers
    ? {
        symbolHeight: 1,
        symbolWidth: 1,
        symbolRadius: 1
      }
    : {}
  const legend = Highcharts.chart(id, {
    chart: {
      animation: false
    },
    colors: defaultColors,
    plotOptions: {
      series: {
        animation: false,
        opacity: 0
      }
    },
    title: {
      text: null
    },
    credits: {
      enabled: false
    },
    exporting: {
      enabled: false
    },
    xAxis: {
      visible: false
    },
    yAxis: {
      visible: false
    },
    legend: {
      ...configLegend,
      maxHeight: 400,
      enabled: true,
      ...legendOptions
    },
    series
  })
  legend.series.forEach(v => {
    if (v?.legendItem?.label?.element) {
      v.legendItem.label.element.onmouseenter = () => onEnter(v)
      v.legendItem.label.element.onmouseleave = () => onLeave(v)
      v.legendItem.label.element.onclick = () => onClick(v)
    }
  })
  return legend
}

export const legendOptions = ({ series, hideMarkers = false, onClick, onEnter, onLeave } = {}) => {
  const legendOptions = hideMarkers
    ? {
        symbolHeight: 1,
        symbolWidth: 1,
        symbolRadius: 1
      }
    : {}
  const legend = {
    chart: {
      animation: false
    },
    colors: defaultColors,
    plotOptions: {
      series: {
        animation: false,
        opacity: 0,
        events: {
          legendItemClick: onClick,
          mouseOver: onEnter,
          mouseOut: onLeave
        }
      }
    },
    title: {
      text: null
    },
    credits: {
      enabled: false
    },
    exporting: {
      enabled: false
    },
    xAxis: {
      visible: false
    },
    yAxis: {
      visible: false
    },
    legend: {
      ...configLegend,
      maxHeight: 400,
      enabled: true,
      ...legendOptions
    },
    series
  }
  return legend
}

const cutName = (source, maxLength = 25, ellipsis = '...') => {
  if (source && source.length > maxLength) {
    return `${source.slice(0, maxLength - ellipsis.length)}${ellipsis}`
  }
  return source
}

export const seriesName = (attributes, attribute, metrics, color) => {
  const attributeText = attributes?.[attribute]?.text || attribute
  return color
    ? `<div style="color:${color};text-shadow: ${color} 0px 0px 1px;"><b>${cutName(attributeText)}</b>${
        metrics ? ` ${metrics.short || metrics.name}` : ''
      }</div>`
    : `<b>${cutName(attributeText)}</b>${metrics ? ` ${metrics.short || metrics.name}` : ''}`
}

// function adjustButtonPosition(container) {
//   if (!container) {
//     return
//   }
//   debugger
//   const button = container.queryElement('.highcharts-contextbutton')
//   const containerRect = getLargestVisibleRect(container) // container.getBoundingClientRect()
//   const buttonRect = getLargestVisibleRect(button)
//   // Calculate new position to bring the button into view
//   const maxVisibleX = containerRect.width - buttonRect.width - 60 // 10px padding

//   // Apply the new position using transform: translate()
//   button.style['transform'] = `translate(${maxVisibleX}px, ${buttonRect.y}px)`
// }

// function getLargestVisibleRect(element) {
//   let rect = element.getBoundingClientRect()

//   // Traverse up the DOM tree to check for scrolling/overflow:hidden elements
//   let parent = element.parentElement

//   let parentRect
//   while (parent) {
//     // If the parent has overflow:hidden/scroll/auto, intersect the rects
//     const overflowStyle = window.getComputedStyle(parent).overflow
//     if (['hidden', 'scroll', 'auto'].includes(overflowStyle)) {
//       parentRect = parent.getBoundingClientRect()
//       rect = {
//         top: Math.max(rect.top, parentRect.top),
//         left: Math.max(rect.left, parentRect.left),
//         bottom: Math.min(rect.bottom, parentRect.bottom),
//         right: Math.min(rect.right, parentRect.right),
//         width: Math.min(rect.right, parentRect.right) - Math.max(rect.left, parentRect.left),
//         height: Math.min(rect.bottom, parentRect.bottom) - Math.max(rect.top, parentRect.top)
//       }
//       break
//     }

//     parent = parent.parentElement
//   }

//   // Finally, intersect with the viewport
//   const viewportWidth = window.innerWidth
//   const viewportHeight = window.innerHeight

//   rect = {
//     top: Math.max(rect.top, 0),
//     left: Math.max(rect.left, 0),
//     bottom: Math.min(rect.bottom, viewportHeight),
//     right: Math.min(rect.right, viewportWidth),
//     width: Math.min(rect.right, viewportWidth) - Math.max(rect.left, 0),
//     height: Math.min(rect.bottom, viewportHeight) - Math.max(rect.top, 0)
//   }

//   // Ensure positive dimensions
//   rect.width = Math.max(0, rect.width)
//   rect.height = Math.max(0, rect.height)

//   return rect
// }
